// @flow

import styles from './SavedSearchesDashboard.less';
import commonStyles from '../SignedInDashboardCommon.less';

import React, { Component } from 'react';
import { connect } from 'react-redux';

import {
  HeartIcon,
  Text,
  TextLink,
  ScreenReaderOnly
} from 'seek-style-guide/react';
import DashboardHeading from '../DashboardHeading/DashboardHeading';
import DashboardItem from '../DashboardItem/DashboardItem';
import DashboardSectionError from '../DashboardSectionError/DashboardSectionError';
import NavLink from 'src/components/NavLink/NavLink';
import ViewAllLink from '../ViewAllLink/ViewAllLink';

import {
  savedSearchClicked,
  getDashboard
} from 'src/store/dashboard/dashboard';
import { get, isUndefined } from 'lodash';

import { savedSearchesPath } from 'src/config';

const mapStateToProps = ({ dashboard: { savedSearches } }) => ({
  savedSearches: get(savedSearches, 'data'),
  totalCount: get(savedSearches, 'totalCount')
});

const mapDispatchToProps = dispatch => ({
  searchClicked: index => dispatch(savedSearchClicked(index)),
  retrySavedSearches: () =>
    dispatch(getDashboard({ sections: 'savedSearches' }))
});

type Props = {
  searchClicked: Function,
  retrySavedSearches?: Function,
  savedSearches?: Array<SavedSearchesExperience>,
  totalCount?: number
};

class SavedSearchesDashboard extends Component<Props> {
  props: Props;

  searchClicked = (index: number) => () => this.props.searchClicked(index);

  renderLink = (
    {
      name,
      query,
      count,
      id
    }: { name: string, query: SearchParams, id: string, count: string },
    index: number
  ) => {
    return (
      <NavLink
        data-automation="saved-search-link"
        component={TextLink}
        children={
          <span>
            <ScreenReaderOnly>
              {`${count} new ${count === 1 ? 'job' : 'jobs'} for:`}
            </ScreenReaderOnly>
            {name}
          </span>
        }
        analytics="dashboard-save-search-click"
        location={{ pathname: '/jobs', query: { ...query, savedsearchid: id } }}
        onClick={this.searchClicked(index)}
      />
    );
  };

  render() {
    const { savedSearches, totalCount, retrySavedSearches } = this.props;
    const label = `Saved ${totalCount === 1 ? 'search' : 'searches'}`;

    const viewAllLinkProps = {
      location: savedSearchesPath,
      analytics: 'dashboard-all-saved-searches-click'
    };

    return (
      <nav
        aria-label={label}
        role="navigation"
        data-automation="savedSearchDashboard"
      >
        <DashboardHeading
          label={label}
          count={totalCount}
          Icon={HeartIcon}
          iconClass={styles.icon}
          className={styles.heading}
          href={savedSearchesPath}
          analytics="dashboard-heading-saved-searches-click"
        />
        {isUndefined(savedSearches) ? (
          <DashboardSectionError
            dataAutomation="savedSearchesSectionError"
            label={label}
            navLinkProps={viewAllLinkProps}
            retryHandler={retrySavedSearches}
          />
        ) : (
          <div>
            {savedSearches.length > 0 ? (
              <div className={commonStyles.items}>
                {savedSearches.map((savedSearch, index) => {
                  const { id, count, summary } = savedSearch;

                  /* eslint-disable react/jsx-no-bind */
                  return (
                    <DashboardItem
                      key={id}
                      heading={() => this.renderLink(savedSearch, index)}
                      subheading={() => (
                        <span aria-hidden="true">{summary}</span>
                      )}
                      count={() => (
                        <span aria-hidden="true">{`${count} new`}</span>
                      )}
                    />
                  );
                  /* eslint-enable react/jsx-no-bind */
                })}
                <ViewAllLink
                  {...viewAllLinkProps}
                  screenReaderText="your saved searches"
                />
              </div>
            ) : (
              <Text secondary small className={commonStyles.noItems}>
                Use the&nbsp;
                <span className={styles.noItems_highlight}>
                  <HeartIcon svgClassName={styles.iconSvg} /> Save&nbsp;search
                </span>
                &nbsp;button below the search results to save your search and
                receive every new job.
              </Text>
            )}
          </div>
        )}
      </nav>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SavedSearchesDashboard);
